import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Close } from '@videoblocks/react-icons';

import { ReduxState } from '../../../../../AppLoader/types';
import AiRiskModal from '../../../../../PopUps/components/AiRiskModal';
import { notifyError } from '../../../../../ReactToastify/containers/ContentEditToast';
import {
  selectUser,
  selectUserSubscriptionUniqueId,
} from '../../../../../auth/AuthSelectors';
import { ContentEditSegment } from '../../../../../auth/AuthTypes';
import { Text } from '../../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import { useScrollLock } from '../../../../../common/hooks/useScrollLock';
import { getPreviewUrl } from '../../../../../common/utils';
import ContentEditAPI from '../../../../ContentEdit/ContentEditAPI';
import {
  closeVideoEditorModal,
  resetModalState,
  selectContentEditSegment,
  selectCreditBalance,
  selectModalState,
  selectShouldShowAIEditFeatures,
  selectVideoEditorModalIsOpen,
  selectVideoEditorModalStockItem,
  setCreditBalance,
  startPolling,
  selectOptIns,
  selectPollingTaskId,
  TaskStatus,
  selectIsPolling,
} from '../../../../ContentEdit/ContentEditSlice';
import CreditConfirmationModal from './CreditConfirmationModal';
import InProgressJobModal from './InProgressJobModal';
import MoreCreditConfirmationModal from './MoreCreditConfirmationModal';
// import CreditPurchaseModal from './CreditPurchaseModal'; currently unused -- we are holding off on implementing modal flow for launch
import VideoEditLoad from './VideoEditLoad';
import VideoEditResult from './VideoEditResult';
import VideoEditorUI from './VideoEditorUI';
import {
  useVideoMetadata,
  useVideoSelection,
  useVideoUI,
  VideoEditorProvider,
} from './context/VideoEditorContext';
import { BroadcastMessage } from './hooks/useCrossTabSync';
import { useVideoEditorSync } from './hooks/useVideoEditorSync';
import { useVideoStatusPolling } from './hooks/useVideoStatusPolling';
import { CREDIT_COST, useCases } from './utils/constants';
import { handlePurchaseCredits } from './utils/purchaseUtils';
import {
  clearModalSessionStorageState,
  clearPollingState,
  removeVideoEditModalParam,
  savePollingState,
} from './utils/stateUtils';
import { VideoMetadata } from './utils/videoUtils';

import './VideoEditorModal.css';

const VideoEditorModalContent = ({
  sendMessage,
}: {
  sendMessage: (message: BroadcastMessage) => void;
}) => {
  const dispatch = useDispatch<ThunkDispatch<ReduxState, unknown, AnyAction>>();
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const selectedStockItem = useSelector(selectVideoEditorModalStockItem);

  const creditBalance = useSelector(selectCreditBalance);
  const modalState = useSelector(selectModalState);

  const userSubscriptionUniqueId = useSelector(selectUserSubscriptionUniqueId);

  const isPolling = useSelector(selectIsPolling);

  const optIns = useSelector(selectOptIns);
  const [isRiskModalOpen, setIsRiskModalOpen] = React.useState(false);
  const [upgradeModalIsOpen, setUpgradeModalIsOpen] = React.useState(false);
  const [showInProgressJobModal, setShowInProgressJobModal] =
    React.useState(false);

  const {
    videoSrc,
    setVideoSrc,
    setVideoMetadata,
    setIsLoadingMetadata,
    setError,
  } = useVideoMetadata();

  const {
    showCreditConfirmation,
    isGeneratingTaskId,
    promptInputRef,
    referenceImage,
    selectedUseCase,
    defaultPlaceholder,
    setShowCreditConfirmation,
    setIsGeneratingTaskId,
    resetPromptOverrides,
  } = useVideoUI();

  const { selection } = useVideoSelection();

  useEffect(() => {
    // on mount, bind key handler
    const closeOnEscapePressed = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', closeOnEscapePressed);
    return () => window.removeEventListener('keydown', closeOnEscapePressed);
  }, []);

  // Update video source when stock item changes
  useEffect(() => {
    if (!selectedStockItem) {
      setVideoSrc(null);
      return;
    }

    // Use the preview URL if available
    if (selectedStockItem.previewUrl) {
      setVideoSrc(selectedStockItem.previewUrl);
    } else {
      setVideoSrc(
        getPreviewUrl(selectedStockItem.id, selectedStockItem.contentClass)
      );
    }
  }, [selectedStockItem, setVideoSrc]);

  // Load video metadata immediately when modal opens
  useEffect(() => {
    if (!isOpen || !videoSrc) {
      setVideoMetadata(null);
      setIsLoadingMetadata(true);
      return;
    }

    const loadMetadata = async () => {
      try {
        setIsLoadingMetadata(true);
        setError(null);

        const video = document.createElement('video');
        video.src = videoSrc;
        video.preload = 'metadata';
        video.muted = true;

        const metadata = await new Promise<VideoMetadata>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Video metadata load timeout'));
          }, 5000);

          video.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout);
            resolve({
              duration: video.duration,
              width: video.videoWidth,
              height: video.videoHeight,
              isVertical: selectedStockItem?.isVertical ?? false,
            });
          });

          video.addEventListener('error', () => {
            clearTimeout(timeout);
            reject(new Error('Video failed to load'));
          });
        });

        setVideoMetadata(metadata);
        setIsLoadingMetadata(false);
      } catch (error) {
        console.error('❌ Video metadata loading failed:', error);
        setError(
          error instanceof Error ? error.message : 'Failed to load video'
        );
        setIsLoadingMetadata(false);
      }
    };

    loadMetadata();
  }, [isOpen, videoSrc, selectedStockItem?.isVertical]);

  const handleGenerateVideo = async (hasOptedIn?: boolean) => {
    if (!optIns.aiContentEdit) {
      setIsRiskModalOpen(!hasOptedIn);
      return;
    }
    if (creditBalance < CREDIT_COST) {
      setUpgradeModalIsOpen(true);
      return;
    }

    // Check for in-progress job
    try {
      const response = await ContentEditAPI.checkVideoEditStatus();
      const result = await response.json();
      const taskStatus = result.data?.runway_task_status;

      // Show modal only for in-progress statuses
      if (
        taskStatus === TaskStatus.RUNNING ||
        taskStatus === TaskStatus.PENDING ||
        taskStatus === TaskStatus.THROTTLED
      ) {
        setShowInProgressJobModal(true);
        return;
      }
    } catch (error) {
      // TODO: handle error
      // continue with generation flow even if check fails
    }

    setShowCreditConfirmation(true);
    Telemetry.increment('ai.contentEdit.buttonClick.generateVideo');
  };

  const handleConfirmCreditSpend = async () => {
    try {
      // Get current prompt value from the input
      const currentPrompt = promptInputRef.current?.getPromptValue() || '';

      // First, generate the video and get the taskId
      setIsGeneratingTaskId(true);
      const response = await ContentEditAPI.generateVideoEdit({
        contentId: selectedStockItem?.contentId ?? null,
        prompt_text: currentPrompt,
        start_time: selection?.startTime || 0,
        duration: selection?.duration || 5,
        image_reference: referenceImage || undefined,
      });

      const result = await response.json();
      const taskId = result.data?.results?.task_id;
      const serverBalance = result.data?.balance;

      // Sync balance from BE whenever the server sends it (success or e.g. 422 insufficient credits)
      if (typeof serverBalance === 'number') {
        dispatch(setCreditBalance(serverBalance));
        sendMessage({
          type: 'CREDITS_UPDATED',
          payload: { newBalance: serverBalance },
        });
      }

      if (!response.ok) {
        notifyError();
        setIsGeneratingTaskId(false);
        return; // Keep confirmation modal open
      }

      if (!taskId) {
        notifyError();
        setIsGeneratingTaskId(false);
        return; // Keep confirmation modal open
      }

      setShowCreditConfirmation(false);

      const selectionPayload = selection
        ? { startTime: selection.startTime, duration: selection.duration }
        : undefined;

      dispatch(
        startPolling({
          stockItem: selectedStockItem,
          taskId,
          promptText: currentPrompt,
          selection: selectionPayload,
        })
      );

      savePollingState({
        lastPromptText: currentPrompt,
        lastSelection: selectionPayload,
      });

      // Broadcast to other tabs that a job was submitted
      sendMessage({
        type: 'JOB_SUBMITTED',
        payload: {
          stockItem: selectedStockItem,
          taskId,
        },
      });

      resetPromptOverrides();
    } catch (error) {
      console.error('Failed to generate video or spend credits:', error);
      notifyError();
    } finally {
      setIsGeneratingTaskId(false);
    }
  };

  const handleCancelCreditSpend = () => {
    setShowCreditConfirmation(false);
  };

  const handleClose = () => {
    dispatch(closeVideoEditorModal());
    clearModalSessionStorageState();
    removeVideoEditModalParam();
    if (modalState === 'result') {
      dispatch(resetModalState());
      clearPollingState();
    }
  };

  const renderModalContent = () => {
    switch (modalState) {
      case 'edit':
        return <VideoEditorUI onGenerateVideo={handleGenerateVideo} />;
      case 'loading':
        return <VideoEditLoad handleClose={handleClose} />;
      case 'result':
        return (
          <VideoEditResult
            handleClose={handleClose}
            contentId={selectedStockItem?.contentId ?? null}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      data-testid="video-editor-modal-backdrop"
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-1000 text-white"
      onMouseDown={handleClose}
    >
      {/* Show UI immediately once metadata is loaded */}
      <div
        data-testid="video-editor-modal-panel"
        className={`video-editor-modal overflow-y-scroll modal-content-scroll flex flex-col items-center w-full h-full ${
          modalState === 'result'
            ? 'result-modal-padding'
            : 'video-editor-modal-padding'
        }`}
        style={{
          backdropFilter: 'blur(40px)',
          backgroundColor: 'rgba(26, 28, 30, 0.60)',
        }}
        onMouseDown={(e) => {
          // prevents the backdrop from being clicked, which would close the modal.
          e.stopPropagation();
        }}
      >
        <div className="w-full flex justify-between gap-2 items-top shrink-0 mb-6 lg:mb-10">
          <img
            className="h-9 w-9"
            src={`${__ASSETS_COMMON_IMAGES_URL__}/logos/storyblocks-icon.svg`}
            alt="Storyblocks Logo"
          />
          <div className="flex flex-col items-center justify-center">
            <Text fontSize="2xl" className="m-0">
              AI Video Editing
            </Text>
            <img
              className="w-28"
              src={`${__ASSETS_COMMON_IMAGES_URL__}/logos/partners/runway.svg`}
              alt="Powered by Runway Logo"
            />
          </div>
          <button
            type="button"
            data-testid="video-editor-modal-close-button"
            className="flex"
            onClick={handleClose}
          >
            <Close className="fill-current h-5 w-5 text-white" />
          </button>
        </div>
        <div className="flex-1 w-full flex flex-col items-center">
          {renderModalContent()}
        </div>

        <AiRiskModal
          isOpen={isRiskModalOpen}
          onClose={() => setIsRiskModalOpen(false)}
          onSuccess={handleGenerateVideo}
        />
        <InProgressJobModal
          isOpen={showInProgressJobModal}
          onClose={() => {
            window.location.reload();
          }}
        />
        <CreditConfirmationModal
          isOpen={showCreditConfirmation}
          creditCost={CREDIT_COST}
          currentBalance={creditBalance}
          onConfirm={handleConfirmCreditSpend}
          onCancel={handleCancelCreditSpend}
          isLoading={isGeneratingTaskId || isPolling}
        />
        <MoreCreditConfirmationModal
          isOpen={upgradeModalIsOpen}
          onConfirm={() =>
            handlePurchaseCredits({
              selectedStockItem,
              selection,
              selectedUseCase,
              defaultPlaceholder,
              promptText: promptInputRef.current?.getPromptValue() || '',
              planId: userSubscriptionUniqueId,
            })
          }
          onCancel={() => setUpgradeModalIsOpen(false)}
        />
      </div>
    </div>
  );
};

const VideoEditorModal = () => {
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const shouldShowAIEditFeatures = useSelector(selectShouldShowAIEditFeatures);
  const contentEditSegment = useSelector(
    selectContentEditSegment
  ) as ContentEditSegment;
  const isPolling = useSelector(selectIsPolling);
  const pollingTaskId = useSelector(selectPollingTaskId);
  const user = useSelector(selectUser);
  useScrollLock(isOpen);
  // Cross-tab synchronization - handles all message types and Redux integration
  const { sendMessage } = useVideoEditorSync();

  // Initiates polling once generation starts and broadcasts success/failure to other tabs
  useVideoStatusPolling(sendMessage);

  // Only show modal for valid segments (hide for disabled)
  const shouldShowModal =
    isOpen && shouldShowAIEditFeatures && contentEditSegment !== 'disabled';

  useEffect(() => {
    // after job starts, bind beacon that notifies server if user leaves (for sending email)
    const sendQueueStatusBeacon = () => {
      let beacon = false;
      if (navigator.sendBeacon) {
        const url = new URL(
          '/api/video/generate-edit/queue-status-check-job',
          location.origin
        );
        url.searchParams.set('userId', String(user?.id ?? ''));
        url.searchParams.set('taskId', pollingTaskId ?? '');

        try {
          beacon = navigator.sendBeacon(url.toString());
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('error sending beacon', err);
          return beacon;
        }
      }

      return beacon;
    };

    if (isPolling) {
      window.addEventListener('beforeunload', sendQueueStatusBeacon);
    }

    return () => {
      window.removeEventListener('beforeunload', sendQueueStatusBeacon);
    };
  }, [isPolling, user?.id, pollingTaskId]);

  if (!shouldShowModal) return null;

  return (
    <VideoEditorProvider initialUseCase={useCases[0]}>
      <VideoEditorModalContent sendMessage={sendMessage} />
    </VideoEditorProvider>
  );
};

export default VideoEditorModal;
